import type { ComponentType, FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import {
	type ClientPropertyArtDetailResponseDTO,
	type ClientPropertyCottageDetailResponseDTO,
	type ClientPropertyFlatDetailResponseDTO,
	type ClientPropertyHouseDetailResponseDTO,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyTypeEnum,
	type ClientPropertyVehicleDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { Art } from "web_myplann/Portfolio/Components/PropertySections/Art";
import { AutoMoto } from "web_myplann/Portfolio/Components/PropertySections/AutoMoto";
import { Cottage } from "web_myplann/Portfolio/Components/PropertySections/Cottage";
import { Flat } from "web_myplann/Portfolio/Components/PropertySections/Flat";
import { House } from "web_myplann/Portfolio/Components/PropertySections/House";
import { Land } from "web_myplann/Portfolio/Components/PropertySections/Land";

export type PropertyDetail =
	| ClientPropertyVehicleDetailResponseDTO
	| ClientPropertyHouseDetailResponseDTO
	| ClientPropertyFlatDetailResponseDTO
	| ClientPropertyCottageDetailResponseDTO
	| ClientPropertyLandDetailResponseDTO
	| ClientPropertyArtDetailResponseDTO;

type SectionProps<T extends PropertyDetail> = {
	data: T;
	currencySign: string;
	documentsId: number;
};

type SectionMap = {
	[ClientPropertyTypeEnum.AutoMoto]: ComponentType<
		SectionProps<ClientPropertyVehicleDetailResponseDTO>
	>;
	[ClientPropertyTypeEnum.Art]: ComponentType<
		SectionProps<ClientPropertyArtDetailResponseDTO>
	>;
	[ClientPropertyTypeEnum.Cottage]: ComponentType<
		SectionProps<ClientPropertyCottageDetailResponseDTO>
	>;
	[ClientPropertyTypeEnum.Flat]: ComponentType<
		SectionProps<ClientPropertyFlatDetailResponseDTO>
	>;
	[ClientPropertyTypeEnum.House]: ComponentType<
		SectionProps<ClientPropertyHouseDetailResponseDTO>
	>;
	[ClientPropertyTypeEnum.Land]: ComponentType<
		SectionProps<ClientPropertyLandDetailResponseDTO>
	>;
};

const SECTION_BY_TYPE: SectionMap = {
	[ClientPropertyTypeEnum.AutoMoto]: AutoMoto,
	[ClientPropertyTypeEnum.Art]: Art,
	[ClientPropertyTypeEnum.Cottage]: Cottage,
	[ClientPropertyTypeEnum.Flat]: Flat,
	[ClientPropertyTypeEnum.House]: House,
	[ClientPropertyTypeEnum.Land]: Land,
};

type Props = {
	property: PropertyDetail;
	currencySign: string;
	documentsId: number;
};

export const PropertySection: FC<Props> = ({
	property,
	currencySign,
	documentsId,
}) => {
	const { isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(documentsId);

	const Section = SECTION_BY_TYPE[property.type as keyof SectionMap] as
		| ComponentType<SectionProps<PropertyDetail>>
		| undefined;

	if (isDocumentsLoading) {
		return <DetailSectionLoader />;
	}

	if (!Section) {
		return null;
	}

	return (
		<Section
			data={property}
			currencySign={currencySign}
			documentsId={documentsId}
		/>
	);
};
